/**
 * The job whiteboard (card AIW-CANVAS).
 *
 * Jacky: *"every job is unique, every cutomer have special needs, we run a AI Agent here, nothing
 * should be like traditional system, emma should be able to record any data in any shape. in any job
 * or opportunity, she is armed with a whiteboard, she can write text, draw diagram, create PDF,
 * anything i ask, she can deliver and register."*
 *
 * So the shape is deliberately loose and the KINDS are open. What is not loose is the envelope: an
 * entry always says what it is, who wrote it and when, and it can point at assets (photos, drawings,
 * generated PDFs) that live in the estate's own asset store — because a whiteboard that cannot say
 * where a fact came from is a rumour.
 */
import { z } from 'zod';
/** Open on purpose. A new kind of entry must be data, never a migration — that is the difference
 *  between "any data in any shape" and a form with extra fields. */
export const WhiteboardEntry = z.object({
    id: z.string().min(1).max(80),
    kind: z.string().min(1).max(40),
    title: z.string().max(300).default(''),
    body: z.string().max(20_000).default(''),
    /** Anything structured the entry needs: table rows, drawing points, checklist items, a PDF's
     *  page list. Free-form by design. */
    data: z.unknown().optional(),
    /** Assets by id — an uploaded photo, a traced plan, a generated PDF. */
    asset_ids: z.array(z.string().uuid()).max(50).default([]),
    created_by: z.string().min(1).max(200),
    created_at: z.string().min(1),
    /** Set when a later entry replaces this one, so nothing is silently lost. */
    superseded_by: z.string().max(80).nullable().default(null),
});
export const WhiteboardDocument = z.object({
    entries: z.array(WhiteboardEntry).max(2000).default([]),
});
export const emptyWhiteboard = () => ({ entries: [] });
/** The kinds the estate already uses, for the picker — NOT a constraint. */
export const WHITEBOARD_KINDS = ['note', 'drawing', 'table', 'photo', 'pdf', 'checklist', 'voice'];
/** Add an entry to a document, returning the new document. Pure, so the command stays testable. */
export function withEntry(document, entry) {
    if (document.entries.some((e) => e.id === entry.id)) {
        throw new Error(`the whiteboard already holds an entry with id ${entry.id}`);
    }
    return { entries: [...document.entries, entry] };
}
/** Replace an entry's content, keeping its id and its place, and marking nothing as deleted —
 *  the previous document still exists as a revision. */
export function withEntryReplaced(document, id, patch) {
    const found = document.entries.find((e) => e.id === id);
    if (!found)
        throw new Error(`no entry ${id} on this whiteboard`);
    return { entries: document.entries.map((e) => (e.id === id ? { ...e, ...patch } : e)) };
}
/** The entries as a person reads them: newest first, with anything superseded still present. */
export function readEntries(document) {
    return [...document.entries].sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id));
}
//# sourceMappingURL=whiteboard.js.map